import { getConfig } from '@/configs/config-accessor';
import { handleGetCollection } from '@/constants/db-collection.constants';
import { IUploadedFile } from '@/utils/upload-file';
import ApiService from './ApiService';
import { IUser } from './user-service';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

export const TaskPriority = ['Low', 'Medium', 'High', 'Critical'] as const;
export const TaskStatus = ['Pending', 'Completed'] as const;

export const TaskType = ['Form Entry'] as const;
export type FormEntryTaskData = { url: string };

export interface ITaskManager {
  _id: string;
  user: IUser['auth'];
  sections: ITaskManagerSection[];
}

export interface ITaskManagerSection {
  _id: string;
  title: string;
  tasks: ITask[];
}

export interface ITask {
  _id: string;
  title: string;
  description: string;
  assignedTo: Pick<IUser, 'auth' | 'image' | 'name'>[];
  createdBy: string;
  priority: (typeof TaskPriority)[number];
  type: (typeof TaskType)[number];
  data: string;
  status: (typeof TaskStatus)[number];
  attachement: IUploadedFile[];
  comments: ITaskComment[];
}

export interface ITaskComment {
  _id: string;
  user: IUser['auth'];
  comment: string;
  createdAt: Date;
  updatedAt: Date;
}

export type GetTaskManagerByUserId_Params = { userId: string };
export type GetTaskManagerByUserId_Response = IResponse<ITaskManager[] | null>;
export const getTaskManagerByUserId = async (params: GetTaskManagerByUserId_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetTaskManagerByUserId_Response, DynamicGet_Body>({ 
    url: `${newApiPrefix}/dynamic/get`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('task-manager'), 
      filter: { user: params.userId } 
    } 
  });
};

export type CreateTask_Body = Omit<ITask, '_id' | 'assignedTo'> & { assignedTo: string[] };
export type CreateTask_Response = IResponse<ITask | null>;
export const createTask = async (body: CreateTask_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<CreateTask_Response, DynamicCreate_Body<CreateTask_Body>>({ 
    url: `${newApiPrefix}/dynamic/create`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('task'), 
      data: body 
    } 
  });
};

export type UpdateTask_Params = { _id: string };
export type UpdateTask_Body = Partial<Omit<ITask, '_id'>>;
export type UpdateTask_Response = IResponse<ITask | null>;
export const updateTask = async (params: UpdateTask_Params, body: UpdateTask_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<UpdateTask_Response, DynamicUpdate_Body<UpdateTask_Body>>({ 
    url: `${newApiPrefix}/dynamic/update`, 
    method: 'patch', 
    data: { 
      collection: handleGetCollection('task'), 
      filter: { _id: params._id }, 
      data: body 
    } 
  });
};

export type GetTaskById_Params = { _id: string };
export type GetTaskById_Response = IResponse<ITask | null>;
export const getTaskById = async (params: GetTaskById_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetTaskById_Response, DynamicGet_Body>({ 
    url: `${newApiPrefix}/dynamic/get`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('task'), 
      filter: { _id: params._id } 
    } 
  });
};
